// src/utils/calendarUtils.js
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

/**
 * Normaliza y formatea una fecha a formato ICS UTC (YYYYMMDDTHHmmssZ)
 */
function safeDateParse(val) {
  if (!val) return new Date();
  if (val.toDate && typeof val.toDate === 'function') return val.toDate();
  const d = new Date(val);
  return isNaN(d.getTime()) ? new Date() : d;
}

function formatIsoForCalendar(date) {
  return date.toISOString().replace(/-|:|\.\d+/g, '');
}

/**
 * Abre Google Calendar (App en Android/iOS o Web) con el evento pre-cargado.
 * Es la forma más rápida y 100% compatible con móviles.
 */
export const openGoogleCalendar = ({ title, description = '', location = 'Sala Virtual Kondify', startDate, endDate }) => {
  try {
    const start = safeDateParse(startDate);
    const end = endDate ? safeDateParse(endDate) : new Date(start.getTime() + 2 * 60 * 60 * 1000);

    const startIso = formatIsoForCalendar(start);
    const endIso = formatIsoForCalendar(end);

    const gUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startIso}/${endIso}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;

    window.open(gUrl, '_blank');
  } catch (err) {
    console.error("Error abriendo Google Calendar:", err);
  }
};

/**
 * Genera y descarga un archivo .ics para Apple Calendar, Outlook o Google Calendar.
 */
export const downloadIcsCalendar = async ({ title, description = '', location = 'Sala Virtual Kondify', startDate, endDate }) => {
  try {
    const start = safeDateParse(startDate);
    const end = endDate ? safeDateParse(endDate) : new Date(start.getTime() + 2 * 60 * 60 * 1000);

    const startStr = formatIsoForCalendar(start);
    const endStr = formatIsoForCalendar(end);

    const cleanTitle = (title || 'Asamblea General').replace(/\n/g, ' ');
    const cleanDesc = (description || '').replace(/\n/g, '\\n');

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Kondify//Asambleas Condominio//ES',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `SUMMARY:${cleanTitle}`,
      `DESCRIPTION:${cleanDesc}`,
      `LOCATION:${location}`,
      `DTSTART:${startStr}`,
      `DTEND:${endStr}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    // Si está en Android / iOS nativo con Capacitor, usar Google Calendar o compartir texto
    if (Capacitor.isNativePlatform()) {
      // En móvil, la mejor UX es abrir directamente la app de Google Calendar
      openGoogleCalendar({ title: cleanTitle, description, location, startDate, endDate });
      return;
    }

    // En navegador Web (Desktop / Mobile Web), descargar .ics
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${cleanTitle.replace(/[^a-zA-Z0-9]/g, '_')}.ics`);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 200);
  } catch (err) {
    console.error("Error generando archivo de calendario:", err);
    // Fallback garantizado
    openGoogleCalendar({ title, description, location, startDate, endDate });
  }
};
